import React, {memo, useEffect, useRef, useState} from 'react';
import PersonAvatar from './PersonAvatar';
import {Resource} from '../../Resource/types';
import {HiCheck, HiOutlineDotsVertical, HiX, HiDownload, HiHome} from 'react-icons/hi';
import useResourcesService from '../../../services/resources';
import {useJwt} from '../../../helpers/common';
import {errorHandler} from '../../../utils/errorHandler';
import useOrganizationService from "../../../services/organization";
import useAzureService from '../../../services/azure';
import useIntegrationStatus from '../../../hooks/useIntegrationStatus';
import {azureTeamSyncRepository} from '../../../services/repositories';
import { useDispatch, useSelector } from 'react-redux';
import { setOrganizationOnboardSettings } from '../../../redux/slices/organization';
import {useUser} from '@clerk/clerk-react';
import {
  OnboardingSpotlight,
  OnboardingStepKeyEnum,
  useOnboardingFlattenedTour,
  useOnboardingNavigator,
  useOnboardingRestart,
  useOnboardingRoadmap,
  useOnboardingStep
} from '../../../components/Onboarding';
import CustomTooltip from '../../../components/common/CustomTooltip';
import TruncatedText from '../../../components/common/TruncatedText';

interface PeopleCardProps {
  index: number;
  resource: Resource;
  handleCardClick: () => void;
  onRequestReinvite: (resource: Resource) => void;
  onRequestDeactivate: (resource: Resource) => void;
}

const PeopleCard: React.FC<PeopleCardProps> = memo(({ index, resource, handleCardClick, onRequestReinvite, onRequestDeactivate }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { activateResource, deactivateResource } = useResourcesService();
  const { fetchTeams, updateOrganization } = useOrganizationService();
  const { fetchAzureTeamSyncDifferences } = useAzureService();
  const { isIntegrationActive } = useIntegrationStatus();
  const azureActive = isIntegrationActive('azure');
  const JWTdata: any = useJwt();
  const org_id = JWTdata.org_id;
  const user_id = JWTdata.user_id;
  const { onboardSettings = {} } = useSelector((s: any) => s.organization);
  const { user } = useUser();
  const currentUserEmail = user?.primaryEmailAddress?.emailAddress;

  const cardWrapperRef = useRef<HTMLDivElement>(null);
  const { isCurrent } = useOnboardingRoadmap();
  

  const { totalSteps: globalTotal, getIndexForStepKey } = useOnboardingFlattenedTour();
  const { goToNextFrom } = useOnboardingNavigator();
  const restartToken = useOnboardingRestart(OnboardingStepKeyEnum.AllocateSkills);

  const isAllocateSkillsTriggered = 
    isCurrent(OnboardingStepKeyEnum.AllocateSkills) || 
    onboardSettings?.current_navigation === OnboardingStepKeyEnum.AllocateSkills;

  const {
    show: showAllocateSkills,
    complete: completeAllocateSkills
  } = useOnboardingStep(OnboardingStepKeyEnum.AllocateSkills, { 
    shouldShow: isAllocateSkillsTriggered
  });

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggleActive = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDropdown(false);
    if (!org_id || !resource?.id) return;
    try {
      if (resource.imported) {
        if (resource.is_deleted) {
          await activateResource(org_id, String(resource.id));
        } else {
          await deactivateResource(org_id, String(resource.id));
        }
        
        await fetchTeams(org_id);
        
        // Reset IndexedDB cache and refresh team sync differences after activation/deactivation
        // Only call sync API if Azure integration is active
        if (org_id && user_id && azureActive) {
          await azureTeamSyncRepository.clearCachedData(org_id, user_id);
          await fetchAzureTeamSyncDifferences(org_id, user_id);
        }
      } else {
        if (resource.is_deleted) {
          onRequestReinvite(resource);
        } else {
          onRequestDeactivate(resource);
        }
      }
    } catch (error) {
      // Service hook already toasted; silent catch short-circuits the cleanup below.
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!resource.is_deleted) {
        handleCardClick();
      }
    }
  };

  const isCurrentUser = resource.email?.toLowerCase() === currentUserEmail?.toLowerCase();

  // Do not render card for pending or expired invites
  if (resource.invite_status === 'pending' || resource.invite_status === 'expired') {
    return null;
  }

  // Get skill color based on index - following Flowbite color philosophy
  const getTeamDisplayName = (team: any) => {
    if (!team?.name) return '';
    const projectName =
      typeof team.project_name === 'string' && team.project_name.trim().length > 0
        ? team.project_name
        : Array.isArray(team.projects) && team.projects.length > 0
          ? team.projects
              .map((project: any) => project?.project_name)
              .filter((name: string | undefined) => typeof name === 'string' && name.trim().length > 0)
              .join(', ')
          : '';
    return projectName ? `${team.name} (${projectName})` : team.name;
  };
  const getSkillColor = (index: number) => {
    const colors = [
      'bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800',

    ];
    return colors[index % colors.length];
  };

  const formatSkillLabel = (skillName: string, maxLength: number = 15): string => {
    if (typeof skillName !== 'string') return '';
    return skillName.length > maxLength ? `${skillName.slice(0, maxLength)}…` : skillName;
  };

  return (
    <CustomTooltip
      title={resource.is_deleted ? "This resource is deactivated and has no login access. They won't be suggested for new work items but previous assignments remain visible." : ""}
      placement="top"
    >
      <div
        className="relative h-full"
        ref={index === 0 ? cardWrapperRef : undefined}
      >
        <div
        className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700
                 transition-all duration-200 overflow-hidden relative h-full
                 ${!resource.is_deleted ? 'hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-lg dark:hover:shadow-2xl cursor-pointer' : 'cursor-not-allowed'}`}
        onClick={!resource.is_deleted ? handleCardClick : (e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        aria-label={`View details for ${resource.name || 'user'}`}
      >
        {/* Header Section */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className={`flex items-center gap-3 flex-1 min-w-0 ${resource.is_deleted ? 'opacity-60 saturate-50' : ''}`}>
              {/* Avatar */}
              <div className="relative">
                <div className={`w-10 h-10 rounded-xl overflow-hidden shadow-md border-2
                  ${!resource.is_deleted
                    ? 'border-green-400 dark:border-green-500'
                    : 'border-red-400 dark:border-red-500'
                  }
                `}>
                  <PersonAvatar
                    name={resource.name || "No Name"}
                    imageUrl={resource.image_url}
                  />
                </div>

                <div
                  className={`absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-800 
                    ${!resource.is_deleted ? 'bg-green-500' : 'bg-red-500'}
                  `}
                  title={!resource.is_deleted ? 'Active Team' : 'Inactive Team'}
                />
              </div>

              {/* Name and Email */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <TruncatedText
                    text={resource.name || "No Name"}
                    className="font-semibold text-gray-900 dark:text-white text-sm leading-tight truncate"
                    as="h3"
                    placement="top-start"
                  />
                </div>
                <TruncatedText
                  text={resource.email || "No email"}
                  className="text-xs text-gray-600 dark:text-gray-400 truncate"
                  as="p"
                  placement="top-start"
                />
              </div>
            </div>

            {/* Options menu - styled to match team card */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDropdown(!showDropdown);
                }}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
                title="More options"
              >
                <HiOutlineDotsVertical className="w-4 h-4" />
              </button>

              {showDropdown && (
                <div className="absolute right-0 top-full mt-2 w-fit bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700  z-50">
                  <button
                    onClick={handleToggleActive}
                    disabled={isCurrentUser && !resource.is_deleted}
                    className={`w-full pl-2 pr-4 py-2 text-left transition-all duration-200 flex items-center gap-2 rounded-lg text-sm ${
                      isCurrentUser && !resource.is_deleted
                        ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
                        : !resource.is_deleted
                          ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                          : 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                    }`}
                    title={isCurrentUser && !resource.is_deleted ? "You cannot deactivate yourself" : ""}
                  >
                    {!resource.is_deleted ? <HiX className="w-4 h-4" /> : <HiCheck className="w-4 h-4" />}
                    <span>{!resource.is_deleted ? 'Deactivate' : 'Activate'}</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Skills Section - Single Line Only */}
          <div className={`mb-3 ${resource.is_deleted ? 'opacity-60 saturate-50' : ''}`}>
            {(() => {
              const skills = resource.skills_name || [];
              if (skills.length === 0) {
                return (
                  <div className="flex items-center h-6 text-xs text-gray-400 dark:text-gray-500 italic">
                    No skills assigned
                  </div>
                );
              }

              const maxSkills = 2; // Show only 2 skills max
              const displayed = skills.slice(0, maxSkills);
              const remaining = skills.length - displayed.length;

            return (
              <div className="flex items-center gap-1.5">
                {displayed.map((skill: string, index: number) => (
                  <CustomTooltip
                    key={skill}
                    title={skill}
                    placement="top"
                  >
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getSkillColor(index)} flex-shrink-0 `}
                    >
                      {formatSkillLabel(skill)}
                    </span>
                  </CustomTooltip>
                ))}
                  {remaining > 0 && (
                    <CustomTooltip
                      title={`Additional skills: ${skills.slice(displayed.length).join(', ')}`}
                      placement="top"
                    >
                      <span
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                               bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 
                               border border-gray-200 dark:border-gray-600 flex-shrink-0 "
                      >
                        +{remaining}
                      </span>
                    </CustomTooltip>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Teams Section - Different Visual Approach */}
          <div className={`border-t border-gray-100 dark:border-gray-700 pt-3 ${resource.is_deleted ? 'opacity-60 saturate-50' : ''}`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300">
                Teams {((resource.teams?.length || 0) > 0 && !resource.is_deleted) && (
                  <span className="text-gray-500 dark:text-gray-400 font-normal">
                    ({resource.teams.length})
                  </span>
                )}
              </h4>
            </div>

            {((resource.teams?.length || 0) > 0 && !resource.is_deleted) ? (
              <div className="space-y-1.5">
                {/* Team indicators - compact dot-based approach */}
                {resource.teams.slice(0, 3).map((team, index) => {
                  const teamLabel = getTeamDisplayName(team);
                  if (!teamLabel) return null;
                  return (
                    <div
                      key={team.id}
                      className={`flex items-center gap-2 group/team ${resource.imported ? 'pr-20' : ''}`}
                      title={teamLabel}
                    >
                      <div className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0 group-hover/team:bg-emerald-500 transition-colors" />
                      <TruncatedText
                        text={teamLabel}
                        className="text-xs text-gray-700 dark:text-gray-300 truncate group-hover/team:text-gray-900 dark:group-hover/team:text-white transition-colors"
                        placement="top-start"
                      />
                    </div>
                  );
                })}

                {/* Show remaining teams count */}
                {resource.teams.length > 3 && (
                   <CustomTooltip
                     title={`Additional teams: ${resource.teams.slice(3).map(t => t.name).join(', ')}`}
                     placement="top"
                   >
                      <div
                        className={`flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors ${resource.imported ? 'pr-16' : ''}`}
                        title={`Additional teams: ${resource.teams.slice(3).map(getTeamDisplayName).filter(Boolean).join(', ')}`}
                      >
                        <div className="w-2 h-2 rounded-full bg-gray-400 flex-shrink-0"></div>
                        <span className="truncate flex-1 min-w-0">+{resource.teams.length - 3} more teams</span>
                      </div>
                  </CustomTooltip>
                )}
              </div>
            ) : (
              <div className="text-xs text-gray-400 dark:text-gray-500 italic">
                No teams assigned
              </div>
            )}
          </div>
        </div>
      </div>

      {resource.imported ? (
        <div className="absolute bottom-4 right-4 flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium border border-blue-400 rounded-md text-blue-600 bg-blue-50 dark:bg-blue-950 dark:border-blue-500 dark:text-blue-400 shadow-sm">
          <HiDownload className="w-3 h-3" />
          <span>Imported</span>
        </div>
      ) : (
        <div className="absolute bottom-4 right-4 flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium border border-emerald-400 rounded-md text-emerald-600 bg-emerald-50 dark:bg-emerald-950 dark:border-emerald-500 dark:text-emerald-400 shadow-sm">
          <HiHome className="w-3 h-3" />
          <span>Cadence</span>
        </div>
      )}


      {/* Onboarding spotlight for allocate skills - only on the first card */}
      {index === 0 && showAllocateSkills && (
        <OnboardingSpotlight
          key={`allocateskills-${restartToken}`}
          targetRef={cardWrapperRef}
          message="Allocate relevant skills to this person to improve planning accuracy."
          onDismiss={() => {
            goToNextFrom(OnboardingStepKeyEnum.AllocateSkills); 
            completeAllocateSkills();
          }}
          onCompleteOnly={() => {
            // Inside spotlight click: complete only, do not advance
            completeAllocateSkills();
          }}
          onClose={async () => {
            const next = { ...(onboardSettings || {}), dismissed: true };
            dispatch(setOrganizationOnboardSettings(next));
            await updateOrganization(JWTdata?.org_id, { onboard_settings: next }, true);
          }}
          variant="spotlight"
          preferredPlacements={['right','bottom','top','left']}
          stepIndex={Math.max(0, getIndexForStepKey(OnboardingStepKeyEnum.AllocateSkills))}
          totalSteps={globalTotal}
          onBack={() => { /* dismiss-only; previous steps are managed in their own views */ }}
          onSkip={() => { completeAllocateSkills(); }}
        />
      )}
      </div>
    </CustomTooltip>
  );
});

export default PeopleCard; 